import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 8;

export const DASHBOARD_EXPORT_SURFACE_ID = "dashboard-export-surface";

function resolveSurface(surfaceId = DASHBOARD_EXPORT_SURFACE_ID): HTMLElement {
  const el = document.getElementById(surfaceId);
  if (!el) {
    throw new Error(`Export surface not found: #${surfaceId}`);
  }
  return el;
}

/** Expand scroll/overflow-clipped tables so html2canvas paints full cell content. */
function prepareCloneForCapture(root: HTMLElement) {
  root.style.setProperty("overflow", "visible", "important");
  root.style.setProperty("max-height", "none", "important");
  root.style.setProperty("height", "auto", "important");
  root.style.setProperty("zoom", "1", "important");
  root.style.setProperty("transform", "none", "important");
  root.style.setProperty("background", "#ffffff", "important");

  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    el.style.setProperty("overflow", "visible", "important");
    el.style.setProperty("overflow-x", "visible", "important");
    el.style.setProperty("overflow-y", "visible", "important");
    el.style.setProperty("max-height", "none", "important");
  });

  // Hide interactive table chrome that is not useful on PDF.
  root.querySelectorAll<HTMLElement>("[data-export-hide], .no-print").forEach((el) => {
    el.style.setProperty("display", "none", "important");
  });

  root.querySelectorAll<HTMLElement>(".react-grid-item").forEach((item) => {
    let tallest = item.offsetHeight;
    item.querySelectorAll<HTMLElement>("table, .MuiTableContainer-root, .dashboard-grid-cell").forEach((child) => {
      tallest = Math.max(tallest, child.scrollHeight, child.offsetHeight);
    });
    // Include nested scroll heights (table body).
    item.querySelectorAll<HTMLElement>("*").forEach((child) => {
      if (child.scrollHeight > child.clientHeight + 2) {
        tallest = Math.max(tallest, child.scrollHeight + 48);
      }
    });
    item.style.setProperty("overflow", "visible", "important");
    item.style.setProperty("height", `${Math.max(item.offsetHeight, tallest)}px`, "important");
  });

  const layout = root.querySelector<HTMLElement>(".react-grid-layout");
  if (layout) {
    let bottom = 0;
    layout.querySelectorAll<HTMLElement>(".react-grid-item").forEach((item) => {
      bottom = Math.max(bottom, item.offsetTop + item.offsetHeight);
    });
    layout.style.setProperty("height", `${bottom + 24}px`, "important");
    layout.style.setProperty("overflow", "visible", "important");
  }
}

async function captureSurface(surface: HTMLElement): Promise<HTMLCanvasElement> {
  // Wait a frame so echarts/tables finish paint before cloning.
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  return html2canvas(surface, {
    backgroundColor: "#ffffff",
    scale: Math.min(2, window.devicePixelRatio || 1.5),
    useCORS: true,
    allowTaint: true,
    logging: false,
    foreignObjectRendering: false,
    scrollX: 0,
    scrollY: 0,
    windowWidth: Math.max(surface.scrollWidth, surface.clientWidth),
    windowHeight: Math.max(surface.scrollHeight, surface.clientHeight),
    onclone: (doc) => {
      const clone = doc.getElementById(surface.id);
      if (clone) {
        prepareCloneForCapture(clone);
      }
    },
  });
}

function canvasPages(canvas: HTMLCanvasElement): HTMLCanvasElement[] {
  const pageWidthPx = canvas.width;
  const usableHeightMm = A4_HEIGHT_MM - MARGIN_MM * 2;
  const usableWidthMm = A4_WIDTH_MM - MARGIN_MM * 2;
  const pageHeightPx = Math.max(1, Math.floor((pageWidthPx * usableHeightMm) / usableWidthMm));

  if (canvas.height <= pageHeightPx) {
    return [canvas];
  }

  const pages: HTMLCanvasElement[] = [];
  let offsetY = 0;
  while (offsetY < canvas.height) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - offsetY);
    const page = document.createElement("canvas");
    page.width = pageWidthPx;
    page.height = sliceHeight;
    const ctx = page.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, page.width, page.height);
      ctx.drawImage(canvas, 0, offsetY, pageWidthPx, sliceHeight, 0, 0, pageWidthPx, sliceHeight);
    }
    pages.push(page);
    offsetY += sliceHeight;
  }
  return pages;
}

function pageImageDataUrl(page: HTMLCanvasElement): string {
  // PNG keeps table text sharper than JPEG.
  return page.toDataURL("image/png");
}

export async function downloadDashboardPdf(options: {
  fileName: string;
  surfaceId?: string;
  title?: string;
}): Promise<void> {
  const surface = resolveSurface(options.surfaceId);
  const canvas = await captureSurface(surface);
  const pages = canvasPages(canvas);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const usableWidth = A4_WIDTH_MM - MARGIN_MM * 2;

  pages.forEach((page, index) => {
    if (index > 0) pdf.addPage();
    const imgHeight = (page.height * usableWidth) / page.width;
    pdf.addImage(pageImageDataUrl(page), "PNG", MARGIN_MM, MARGIN_MM, usableWidth, imgHeight, undefined, "FAST");
  });

  if (options.title) {
    pdf.setProperties({ title: options.title });
  }
  pdf.save(options.fileName.endsWith(".pdf") ? options.fileName : `${options.fileName}.pdf`);
}

export async function printDashboardSurface(options?: {
  surfaceId?: string;
  title?: string;
}): Promise<void> {
  const surface = resolveSurface(options?.surfaceId);
  const canvas = await captureSurface(surface);
  // Slice to A4 aspect so each printed sheet is one full A4 page.
  const pages = canvasPages(canvas);
  const title = options?.title || document.title;
  const imagesHtml = pages
    .map(
      (page, index) =>
        `<section class="page" aria-label="Page ${index + 1}">
          <img src="${pageImageDataUrl(page)}" alt="${title} — ${index + 1}/${pages.length}" />
        </section>`
    )
    .join("");

  const printWindow = window.open("", "_blank", "noopener,noreferrer,width=900,height=1200");
  if (!printWindow) {
    throw new Error("Print window blocked");
  }

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title.replace(/</g, "&lt;")}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      box-sizing: border-box;
      width: ${A4_WIDTH_MM}mm;
      height: ${A4_HEIGHT_MM}mm;
      margin: 0 auto;
      padding: ${MARGIN_MM}mm;
      display: flex;
      align-items: center;
      justify-content: center;
      page-break-after: always;
      break-after: page;
      overflow: hidden;
      background: #fff;
    }
    .page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .page img {
      display: block;
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
      object-position: top center;
    }
    @media screen {
      body { background: #e8e8e8; padding: 12px 0; }
      .page {
        box-shadow: 0 2px 12px rgba(0,0,0,0.18);
        margin-bottom: 12px;
      }
    }
  </style>
</head>
<body>${imagesHtml}</body>
</html>`);
  printWindow.document.close();

  await new Promise<void>((resolve) => {
    const finish = () => resolve();
    const imgs = Array.from(printWindow.document.images);
    if (!imgs.length) {
      window.setTimeout(finish, 200);
      return;
    }
    let remaining = imgs.length;
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    imgs.forEach((img) => {
      if (img.complete) done();
      else {
        img.onload = done;
        img.onerror = done;
      }
    });
    window.setTimeout(finish, 1500);
  });

  printWindow.focus();
  printWindow.print();
  window.setTimeout(() => {
    try {
      printWindow.close();
    } catch {
      /* ignore */
    }
  }, 500);
}
